import {
  ARCHIVAL_STATUS_ARCHIVED,
  ARCHIVAL_STATUS_RESTORING,
} from '../constants/pipelineConstants';
import type { ArchivalStatus as ArchivalStatusType } from 'src/@types';

export interface ResourceEntity {
  archival_status?: ArchivalStatusType;
  [key: string]: any;
}

export interface ValidationResult {
  canActivate: boolean;
  reason: string | null;
}

/**
 * Checks if a resource can be activated based on its archival status
 */
export const canActivateResource = (
  resource: ResourceEntity | null | undefined,
): ValidationResult => {
  if (!resource) {
    return { canActivate: false, reason: 'Resource not found' };
  }

  const archivalStatus = resource.archival_status;

  if (archivalStatus === ARCHIVAL_STATUS_ARCHIVED) {
    return {
      canActivate: false,
      reason: 'Cannot activate archived resource. Please restore the Flow first.',
    };
  }

  if (archivalStatus === ARCHIVAL_STATUS_RESTORING) {
    return {
      canActivate: false,
      reason: 'Flow is currently being restored. Please wait for restoration to complete.',
    };
  }

  return { canActivate: true, reason: null };
};

/**
 * Checks if a resource can be updated (name, description) based on its archival status
 */
export const canUpdateResource = (
  resource: ResourceEntity | null | undefined,
): ValidationResult => {
  if (!resource) {
    return { canActivate: false, reason: 'Resource not found' };
  }

  const archivalStatus = resource.archival_status;

  if (archivalStatus === ARCHIVAL_STATUS_ARCHIVED) {
    return {
      canActivate: false,
      reason: 'Cannot update archived resource. Please restore the Flow first.',
    };
  }

  if (archivalStatus === ARCHIVAL_STATUS_RESTORING) {
    return {
      canActivate: false,
      reason: 'Flow is currently being restored. Please wait for restoration to complete.',
    };
  }

  return { canActivate: true, reason: null };
};

/**
 * Creates action validation object for pause/activate actions
 */
export interface ActionValidation {
  disable: boolean;
  disableMessage?: string;
}

export const createActionValidator = (
  resource: ResourceEntity | null | undefined,
  isPausing: boolean = false,
  isActivating: boolean = false,
): ActionValidation => {
  const archivedStatusValidation = canActivateResource(resource);

  if (archivedStatusValidation.canActivate === false) {
    return {
      disable: true,
      disableMessage: archivedStatusValidation.reason || undefined,
    };
  }

  if (isPausing) {
    return {
      disable: true,
      disableMessage: 'Flow is pausing',
    };
  }

  if (isActivating) {
    return {
      disable: true,
      disableMessage: 'Flow is activating',
    };
  }

  return {
    disable: false,
  };
};

/**
 * Creates delete action validation object for resources
 * Validates that resources are not ACTIVE before allowing deletion
 */
export const createDeleteActionValidator = (
  resourceStatus: string | undefined,
  isDeleting: boolean = false,
  isPausing: boolean = false,
  disableMessage: string,
): ActionValidation => {
  if (isDeleting || isPausing) {
    return {
      disable: true,
      disableMessage: isDeleting ? 'Deleting...' : 'Pausing...',
    };
  }

  if (resourceStatus === 'ACTIVE') {
    return {
      disable: true,
      disableMessage,
    };
  }

  // Allow deletion for PAUSED, INIT, or other non-active states
  return {
    disable: false,
  };
};
